<script setup lang="ts">
import { computed, ref } from 'vue'
import { calculatePaye } from '@/calculators/paye/calculation'
import Input from '@/components/ui/Input.vue'
import Select from '@/components/ui/Select.vue'
import ResultCard from '@/components/calculator/ResultCard.vue'

const grossPay = ref(35_000)
const period = ref<'monthly' | 'annual'>('monthly')

const result = computed(() => {
  return calculatePaye(
    {
      grossPay: Number(grossPay.value) || 0,
      grossPayPeriod: period.value,
      ageBand: 'under65',
      medicalDependants: 0,
      pensionContribution: 0,
      pensionPeriod: 'monthly',
      medicalSchemeMember: false,
    },
    { taxYear: '2027', currency: 'ZAR' }
  )
})
</script>

<template>
  <div class="hero-demo">
    <div class="hero-demo__header">
      <span class="hero-demo__badge">Try it now</span>
      <h3 class="hero-demo__title">Estimate your take-home pay</h3>
    </div>
    <div class="hero-demo__inputs">
      <Input
        id="hero-gross"
        label="Gross salary"
        type="number"
        :model-value="grossPay"
        unit="R"
        placeholder="35000"
        @update:model-value="(v) => (grossPay = Number(v))"
      />
      <Select
        id="hero-period"
        label="Period"
        :model-value="period"
        :options="[
          { value: 'monthly', label: 'Monthly' },
          { value: 'annual', label: 'Annual' },
        ]"
        @update:model-value="(v) => (period = v as 'monthly' | 'annual')"
      />
    </div>
    <dl class="hero-demo__results">
      <ResultCard label="Take-home pay" :value="result.netMonthly" format="currency" highlighted />
      <ResultCard label="Tax payable" :value="result.monthlyPaye" format="currency" />
    </dl>
    <p class="hero-demo__note">
      Based on 2026/27 SARS tax tables. This is a simplified estimate — the full calculator lets you
      add pension, medical aid and age brackets.
    </p>
  </div>
</template>

<style scoped>
.hero-demo {
  padding: var(--space-6);
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
}

.hero-demo__header {
  margin-bottom: var(--space-4);
}

.hero-demo__badge {
  display: inline-block;
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-full);
  background: linear-gradient(135deg, var(--color-accent-light), var(--color-accent));
  color: var(--color-text);
  font-size: var(--text-xs);
  font-weight: var(--font-semibold);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.hero-demo__title {
  margin-top: var(--space-2);
  font-size: var(--text-lg);
  font-weight: var(--font-bold);
  color: var(--color-text);
}

.hero-demo__inputs {
  display: grid;
  gap: var(--space-4);
  margin-bottom: var(--space-5);
}

.hero-demo__results {
  display: grid;
  /* Collapses to a single column on narrow phones instead of
     forcing two cramped ~150px columns. */
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: var(--space-3);
  margin: 0;
}

.hero-demo__note {
  margin-top: var(--space-4);
  font-size: var(--text-xs);
  color: var(--color-text-muted);
  line-height: var(--leading-relaxed);
}
</style>
